import { useAuth } from 'react-oidc-context';
import { useTenant } from '@parcel2go/npm-p2g-app';
import { UnauthorisedResponse } from '~/lib/api/unauthorised-response';
import { getApiPath } from './get-api-path';
import { ApiResponse } from '../api-response';
import { authedFetch } from '../authed-fetch';
import { parseResult } from '../parse-result';
import {
  Address,
  CreateMeProductRequest,
  CreateMeProductResponse,
  CustomerSettingValueType,
  DeleteAddressResponse,
  DeleteMeProductResponse,
  GetAchievementsResponse,
  GetCustomerSettingResponse,
  GetMarketingPreferencesResponse,
  GetMeProductsResponse,
  GetMyDetailsResponse,
  GetMyExtendedDetailsResponse,
  GetPrePayBalanceResponse,
  GetPrePayHistoryResponse,
  GetRefundsResponse,
  GetSearchTokenResponse,
  GetSurchargesResponse,
  MyDetails,
  SearchAddressesResponse,
  UpdateMarketingPreferencesResponse,
  UpdateMeProductResponse,
  UpsertAddressResponse
} from './types';
import { useCallback } from 'react';

export interface IApiResponse<T> {
  success: boolean;
  result: T | null;
}

export function useMeApi() {
  const { user } = useAuth();
  const { tenant } = useTenant();

  const getSearchToken = useCallback(async (): Promise<ApiResponse<GetSearchTokenResponse>> => {
    if (!user?.access_token) {
      return UnauthorisedResponse;
    }
    const url = getApiPath(tenant, `/search`);
    const response = await authedFetch(tenant, url, {}, user?.access_token);
    return parseResult<GetSearchTokenResponse>(response);
  }, [user?.access_token, tenant]);

  const getOptIns = useCallback(async (): Promise<ApiResponse<GetMarketingPreferencesResponse>> => {
    if (!user?.access_token) {
      return UnauthorisedResponse;
    }
    const url = getApiPath(tenant, `/comms`);
    const response = await authedFetch(tenant, url, {}, user?.access_token);
    return parseResult<GetMarketingPreferencesResponse>(response);
  }, [user?.access_token, tenant]);

  const getAchievements = useCallback(
    async (achievements: string[]): Promise<ApiResponse<GetAchievementsResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getApiPath(tenant, `/achievement?achievements=${achievements.join(',')}`);
      const response = await authedFetch(tenant, url, {}, user?.access_token);
      return parseResult<GetAchievementsResponse>(response);
    },
    [user?.access_token, tenant]
  );

  const addAchievements = useCallback(
    async (achievements: string[]): Promise<ApiResponse<GetAchievementsResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getApiPath(tenant, `/achievement`);
      const response = await authedFetch(
        tenant,
        url,
        { method: 'POST', body: JSON.stringify({ achievements: achievements }) },
        user?.access_token
      );
      return parseResult<GetAchievementsResponse>(response);
    },
    [user?.access_token, tenant]
  );

  const getPrePayHistory = useCallback(
    async (take: number, skip: number): Promise<ApiResponse<GetPrePayHistoryResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getApiPath(tenant, `/prepay?take=${take}&skip=${skip}`);
      const response = await authedFetch(tenant, url, {}, user?.access_token);
      return parseResult<GetPrePayHistoryResponse>(response);
    },
    [user?.access_token, tenant]
  );

  const getRefunds = useCallback(async (): Promise<ApiResponse<GetRefundsResponse>> => {
    if (!user?.access_token) {
      return UnauthorisedResponse;
    }
    const url = getApiPath(tenant, `/refund`);
    const response = await authedFetch(tenant, url, {}, user?.access_token);
    return parseResult<GetRefundsResponse>(response);
  }, [user?.access_token, tenant]);

  const getUserSettings = useCallback(
    async (feature?: string): Promise<ApiResponse<GetCustomerSettingResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getApiPath(
        tenant,
        feature ? `/user-setting?setting=${encodeURIComponent(feature)}` : `/user-setting`
      );
      const response = await authedFetch(tenant, url, {}, user?.access_token);
      return parseResult<GetCustomerSettingResponse>(response);
    },
    [user?.access_token, tenant]
  );

  const upsertUserSetting = useCallback(
    async (feature: string, value: CustomerSettingValueType): Promise<ApiResponse<GetCustomerSettingResponse>> => {
      if (!user?.access_token || !feature || value === undefined || value === null) {
        return UnauthorisedResponse;
      }
      const url = getApiPath(tenant, `/user-setting?setting=${encodeURIComponent(feature)}`);
      const response = await authedFetch(
        tenant,
        url,
        { method: 'POST', body: JSON.stringify(value) },
        user?.access_token
      );
      return parseResult<GetCustomerSettingResponse>(response);
    },
    [user?.access_token, tenant]
  );

  const getMyDetails = useCallback(async (): Promise<ApiResponse<GetMyDetailsResponse>> => {
    if (!user?.access_token) {
      return UnauthorisedResponse;
    }
    const url = getApiPath(tenant, `/mydetails`);
    const response = await authedFetch(tenant, url, {}, user?.access_token);
    return parseResult<GetMyDetailsResponse>(response);
  }, [user?.access_token, tenant]);

  const getMyExtendedDetails = useCallback(async (): Promise<ApiResponse<GetMyExtendedDetailsResponse>> => {
    if (!user?.access_token) {
      return UnauthorisedResponse;
    }
    const url = getApiPath(tenant, `/mydetails/extended`);
    const response = await authedFetch(tenant, url, {}, user?.access_token);
    return parseResult<GetMyExtendedDetailsResponse>(response);
  }, [user?.access_token, tenant]);

  const updateMyDetails = useCallback(
    async (model: MyDetails): Promise<ApiResponse<GetMyDetailsResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getApiPath(tenant, `/mydetails`);
      const response = await authedFetch(
        tenant,
        url,
        { method: 'PUT', body: JSON.stringify(model) },
        user?.access_token
      );
      return parseResult<GetMyDetailsResponse>(response);
    },
    [user?.access_token, tenant]
  );

  const getSurcharges = useCallback(
    async (take: number, skip: number): Promise<ApiResponse<GetSurchargesResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getApiPath(tenant, `/surcharge?take=${take}&skip=${skip}`);
      const response = await authedFetch(tenant, url, {}, user?.access_token);
      return parseResult<GetSurchargesResponse>(response);
    },
    [user?.access_token, tenant]
  );

  const searchAddresses = useCallback(
    async (take: number, skip: number, query?: string): Promise<ApiResponse<SearchAddressesResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getApiPath(
        tenant,
        query
          ? `/address?take=${take}&skip=${skip}&query=${encodeURIComponent(query)}`
          : `/address?take=${take}&skip=${skip}`
      );
      const response = await authedFetch(tenant, url, {}, user?.access_token);
      return parseResult<SearchAddressesResponse>(response);
    },
    [user?.access_token, tenant]
  );

  // Api is written so that default address is first address when no params are supplied
  const getDefaultAddress = useCallback(async (): Promise<ApiResponse<SearchAddressesResponse>> => {
    if (!user?.access_token) {
      return UnauthorisedResponse;
    }
    const url = getApiPath(tenant, `/address?take=1&skip=0`);
    const response = await authedFetch(tenant, url, {}, user?.access_token);
    return parseResult<SearchAddressesResponse>(response);
  }, [user?.access_token, tenant]);

  const deleteAddress = useCallback(
    async (id: number): Promise<ApiResponse<DeleteAddressResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getApiPath(tenant, `/address/${id}`);
      const response = await authedFetch(tenant, url, { method: 'DELETE' }, user?.access_token);
      return parseResult<DeleteAddressResponse>(response);
    },
    [user?.access_token, tenant]
  );

  const upsertAddress = useCallback(
    async (address: Address): Promise<ApiResponse<UpsertAddressResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getApiPath(tenant, `/address`);
      const response = await authedFetch(
        tenant,
        url,
        { method: 'POST', body: JSON.stringify({ address: address }) },
        user?.access_token
      );
      return parseResult<UpsertAddressResponse>(response);
    },
    [user?.access_token, tenant]
  );

  const getProducts = useCallback(async (): Promise<ApiResponse<GetMeProductsResponse>> => {
    if (!user?.access_token) {
      return UnauthorisedResponse;
    }
    const url = getApiPath(tenant, `/product`);
    const response = await authedFetch(tenant, url, {}, user?.access_token);
    return parseResult<GetMeProductsResponse>(response);
  }, [user?.access_token, tenant]);

  const createProduct = useCallback(
    async (product: CreateMeProductRequest): Promise<ApiResponse<CreateMeProductResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getApiPath(tenant, `/product`);
      const response = await authedFetch(
        tenant,
        url,
        { method: 'POST', body: JSON.stringify({ product: product }) },
        user?.access_token
      );
      return parseResult<CreateMeProductResponse>(response);
    },
    [user?.access_token, tenant]
  );

  const updateProduct = useCallback(
    async (id: number, product: CreateMeProductRequest): Promise<ApiResponse<UpdateMeProductResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getApiPath(tenant, `/product/${id}`);
      const response = await authedFetch(
        tenant,
        url,
        { method: 'PUT', body: JSON.stringify({ product: product }) },
        user?.access_token
      );
      return parseResult<UpdateMeProductResponse>(response);
    },
    [user?.access_token, tenant]
  );

  const deleteProduct = useCallback(
    async (id: number): Promise<ApiResponse<DeleteMeProductResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getApiPath(tenant, `/product/${id}`);
      const response = await authedFetch(tenant, url, { method: 'DELETE' }, user?.access_token);
      return parseResult<DeleteMeProductResponse>(response);
    },
    [user?.access_token, tenant]
  );

  const getPrePayBalance = useCallback(async (): Promise<ApiResponse<GetPrePayBalanceResponse>> => {
    if (!user?.access_token) {
      return UnauthorisedResponse;
    }
    const url = getApiPath(tenant, `/prepay/balance`);
    const response = await authedFetch(tenant, url, {}, user?.access_token);
    return parseResult<GetPrePayBalanceResponse>(response);
  }, [user?.access_token, tenant]);

  const updateOptIns = useCallback(
    async (selections: { [key: string]: boolean }): Promise<ApiResponse<UpdateMarketingPreferencesResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getApiPath(tenant, `/comms`);
      const response = await authedFetch(
        tenant,
        url,
        { method: 'POST', body: JSON.stringify({ selections: selections }) },
        user?.access_token
      );
      return parseResult<UpdateMarketingPreferencesResponse>(response);
    },
    [user?.access_token, tenant]
  );

  return {
    getSearchToken,
    getOptIns,
    updateOptIns,
    getPrePayHistory,
    getPrePayBalance,
    searchAddresses,
    getDefaultAddress,
    deleteAddress,
    getSurcharges,
    getMyDetails,
    getMyExtendedDetails,
    updateMyDetails,
    getRefunds,
    upsertAddress,
    getUserSettings,
    upsertUserSetting,
    getAchievements,
    addAchievements,
    getProducts,
    createProduct,
    updateProduct,
    deleteProduct
  } as const;
}
